import { BOOL_COLUMN_TYPE } from "../types/constants";
import { getFilterTypeFromResultColumn } from "../types/filterTypes";
import { isDateRangeSet } from "../utils/dateUtils";
import {
  formatDateInput,
  parseDateInput,
  toggleSelectedValues,
} from "../utils/filterSpecUtils";
import { filterValueMultiplier } from "../utils/numberUtils";
import {
  buildPerSourceSummarySpecs,
  buildPreAggFilterSpecs,
  buildPreAggSummarySpec,
  pruneFiltersToColumns,
} from "../utils/reportBuilderUtils";
import { withUniqueColumnNames } from "../utils/specBuilderSupport";
import { useColumnCounts, useFilterStats } from "./useColumnFilterCounts";
import * as labels from "config.labels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const identity = (report) => report;

// The pre-aggregation filter machinery of a report over sourceTables: the
// three filter maps, the summary run that feeds the filter panel, and the
// props the panel popover needs. wrapReport is applied to the summary report
// before it is run, so a caller can embed sources the back-end cannot resolve.
export const usePreAggFilters = ({
  sourceTables,
  joinConfigs,
  outputColumns,
  getColDef,
  runImmediateReport,
  wrapReport = identity,
  dateFormat,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  // Two sources can contribute a column of the same name, so a filter is held
  // against the name the column ends up with — the second becomes "<name>_2" —
  // rather than the name it shares.
  const namedColumns = useMemo(() => withUniqueColumnNames(outputColumns), [
    outputColumns,
  ]);
  const [preAggValueFilters, setPreAggValueFilters] = useState({});
  const [preAggDateFilters, setPreAggDateFilters] = useState({});
  const [preAggNumberFilters, setPreAggNumberFilters] = useState({});

  const [filterPanelAnchorEl, setFilterPanelAnchorEl] = useState(null);
  const [filterPanelColumn, setFilterPanelColumn] = useState(null);
  const [panelSelectedValues, setPanelSelectedValues] = useState(new Set());
  const [panelDateRange, setPanelDateRange] = useState({
    from: null,
    to: null,
  });
  const [panelNumberRange, setPanelNumberRange] = useState({
    min: null,
    max: null,
  });
  const [panelValueSearch, setPanelValueSearch] = useState("");

  const [summaryResults, setSummaryResults] = useState(null);
  const [summaryColumnTypes, setSummaryColumnTypes] = useState({});
  const [summaryFailed, setSummaryFailed] = useState(false);
  const summaryRequestIdRef = useRef(0);

  // Pre-aggregation filters are keyed by output column name alone, so they are
  // pruned against the surviving output columns rather than the source
  // selection: the maps carry no source table, and a filter whose column is not
  // an output column is already dropped when the specification is built.
  useEffect(() => {
    const columnNames = new Set(namedColumns.map((col) => col.column));
    const pruneToColumns = (prev) => pruneFiltersToColumns(prev, columnNames);
    setPreAggValueFilters(pruneToColumns);
    setPreAggDateFilters(pruneToColumns);
    setPreAggNumberFilters(pruneToColumns);
  }, [namedColumns]);

  const namedColumnDef = useCallback(
    (colName) => {
      const direct = getColDef(colName);
      if (direct) return direct;
      const named = namedColumns.find((col) => col.column === colName);
      return named ? getColDef(named.sourceColumn ?? named.column) : null;
    },
    [getColDef, namedColumns]
  );

  const getFilterType = useCallback(
    (colName) => {
      const def = namedColumnDef(colName);
      if (def) return getFilterTypeFromResultColumn(def.type, def.subType);
      const summaryType = summaryColumnTypes[colName];
      return getFilterTypeFromResultColumn(
        summaryType?.type,
        summaryType?.subType
      );
    },
    [namedColumnDef, summaryColumnTypes]
  );

  const preAggFilterCols = useMemo(
    () =>
      new Set([
        ...Object.keys(preAggValueFilters),
        ...Object.keys(preAggDateFilters),
        ...Object.keys(preAggNumberFilters),
      ]),
    [preAggValueFilters, preAggDateFilters, preAggNumberFilters]
  );

  const openFilterPanel = useCallback(
    (colName, anchorEl) => {
      setFilterPanelAnchorEl(anchorEl);
      setFilterPanelColumn(colName);
      setPanelSelectedValues(new Set(preAggValueFilters[colName] ?? []));
      setPanelDateRange(preAggDateFilters[colName] ?? { from: null, to: null });
      setPanelNumberRange(
        preAggNumberFilters[colName] ?? { min: null, max: null }
      );
      setPanelValueSearch("");
    },
    [preAggValueFilters, preAggDateFilters, preAggNumberFilters]
  );

  const closeFilterPanel = useCallback(() => {
    setFilterPanelAnchorEl(null);
    setFilterPanelColumn(null);
  }, []);

  const applyPreAggFilter = useCallback(() => {
    if (!filterPanelColumn) return;
    const type = getFilterType(filterPanelColumn);
    const col = filterPanelColumn;
    const dropKey = (setter) =>
      setter((prev) => {
        const n = { ...prev };
        delete n[col];
        return n;
      });
    if (type === "date") {
      if (isDateRangeSet(panelDateRange)) {
        setPreAggDateFilters((prev) => ({ ...prev, [col]: panelDateRange }));
      } else {
        dropKey(setPreAggDateFilters);
      }
      dropKey(setPreAggValueFilters);
      dropKey(setPreAggNumberFilters);
    } else if (type === "number") {
      if (panelNumberRange.min !== null || panelNumberRange.max !== null) {
        setPreAggNumberFilters((prev) => ({
          ...prev,
          [col]: panelNumberRange,
        }));
      } else {
        dropKey(setPreAggNumberFilters);
      }
      dropKey(setPreAggValueFilters);
      dropKey(setPreAggDateFilters);
    } else {
      if (panelSelectedValues.size > 0) {
        setPreAggValueFilters((prev) => ({
          ...prev,
          [col]: new Set(panelSelectedValues),
        }));
      } else {
        dropKey(setPreAggValueFilters);
      }
      dropKey(setPreAggDateFilters);
      dropKey(setPreAggNumberFilters);
    }
    closeFilterPanel();
  }, [
    filterPanelColumn,
    getFilterType,
    panelDateRange,
    panelNumberRange,
    panelSelectedValues,
    closeFilterPanel,
  ]);

  const buildPreAggFilters = useCallback(
    () =>
      buildPreAggFilterSpecs(
        namedColumns,
        preAggValueFilters,
        preAggDateFilters,
        preAggNumberFilters,
        (col) => namedColumnDef(col)?.type ?? summaryColumnTypes[col]?.type
      ),
    [
      namedColumns,
      preAggValueFilters,
      preAggDateFilters,
      preAggNumberFilters,
      namedColumnDef,
      summaryColumnTypes,
    ]
  );

  const fetchPreAggSummary = useCallback(async () => {
    const requestId = ++summaryRequestIdRef.current;
    const summarySpec = buildPreAggSummarySpec(
      sourceTables,
      joinConfigs,
      outputColumns
    );
    // The joined summary is the one to have, because it is the report's own
    // shape. It cannot be built until the joins fold the sources into a single
    // result, so until then each source is summarised on its own rather than
    // leaving every filter panel empty.
    const specs = summarySpec
      ? [summarySpec]
      : buildPerSourceSummarySpecs(sourceTables, outputColumns);
    if (specs.length === 0) {
      setSummaryResults(null);
      setSummaryColumnTypes({});
      setSummaryFailed(false);
      return;
    }
    try {
      const responses = await Promise.all(
        specs.map(async (specification) => {
          const report = await wrapReport({
            type: "SINGLE_TABLE_REPORT",
            displayName: "",
            description: "",
            specification,
          });
          return runImmediateReport({
            report,
            outputType: "SUMMARISING",
          }).unwrap();
        })
      );
      if (requestId !== summaryRequestIdRef.current) return;
      const summary = {};
      const colTypes = {};
      for (const data of responses) {
        Object.assign(summary, data[0]?.tableSummary ?? {});
        for (const col of data[0]?.columns ?? []) {
          colTypes[col.internalName] = {
            type: col.type?.type ?? null,
            subType: col.type?.subType ?? null,
            enumerations: col.type?.enumerations ?? null,
          };
        }
      }
      setSummaryResults(Object.keys(summary).length > 0 ? summary : null);
      setSummaryColumnTypes(colTypes);
      setSummaryFailed(false);
    } catch {
      if (requestId !== summaryRequestIdRef.current) return;
      // Keep the values already on hand and say so in the panel, rather than
      // silently emptying every filter list.
      setSummaryFailed(true);
    }
  }, [
    sourceTables,
    joinConfigs,
    outputColumns,
    runImmediateReport,
    wrapReport,
  ]);

  useEffect(() => {
    fetchPreAggSummary();
  }, [fetchPreAggSummary]);

  const filterPanelFilterType = filterPanelColumn
    ? getFilterType(filterPanelColumn)
    : "value";
  const filterPanelColDef = filterPanelColumn
    ? namedColumnDef(filterPanelColumn)
    : null;
  const filterPanelEnumerations = useMemo(
    () =>
      summaryColumnTypes[filterPanelColumn]?.enumerations ??
      (filterPanelColDef?.inputType ?? filterPanelColDef?.type)?.enumerations ??
      [],
    [filterPanelColDef, filterPanelColumn, summaryColumnTypes]
  );
  const filterPanelColumnSummary = useMemo(
    () =>
      filterPanelColumn ? summaryResults?.[filterPanelColumn] ?? null : null,
    [filterPanelColumn, summaryResults]
  );
  const filterPanelIsEnum = filterPanelEnumerations.length > 0;
  const getFilterPanelEnumDisplayValue = useCallback(
    (v) => {
      const e = filterPanelEnumerations.find(
        (en) => (en.internalName ?? en) === v
      );
      return e?.displayName ?? v;
    },
    [filterPanelEnumerations]
  );
  const {
    values: filterPanelUniqueValues,
    counts: filterPanelValueCounts,
  } = useColumnCounts({
    column: filterPanelColumn ?? "",
    searchQuery: panelValueSearch,
    isEnumColumn: filterPanelIsEnum,
    getEnumDisplayValue: getFilterPanelEnumDisplayValue,
    summaryValues: filterPanelColumnSummary,
  });
  const {
    dateBlankCount: filterPanelDateBlankCount,
    numberBounds: filterPanelNumberBounds,
    histogramBins: filterPanelHistogramBins,
  } = useFilterStats({
    column: filterPanelColumn ?? "",
    columnType: filterPanelFilterType,
    summary: filterPanelColumnSummary,
  });
  const filterPanelDisplayName =
    filterPanelColDef?.displayName ?? filterPanelColumn ?? "";
  const filterPanelSubType =
    filterPanelColDef?.subType ??
    summaryColumnTypes[filterPanelColumn]?.subType ??
    null;

  const filterMaps = useMemo(
    () => ({
      value: preAggValueFilters,
      date: preAggDateFilters,
      number: preAggNumberFilters,
    }),
    [preAggValueFilters, preAggDateFilters, preAggNumberFilters]
  );

  const setFilterMaps = useCallback(({ value, date, number }) => {
    setPreAggValueFilters(value ?? {});
    setPreAggDateFilters(date ?? {});
    setPreAggNumberFilters(number ?? {});
  }, []);

  const panelActions = {
    show: true,
    onSave: applyPreAggFilter,
    onCancel: closeFilterPanel,
  };

  const panelProps = {
    anchorEl: filterPanelAnchorEl,
    onClose: closeFilterPanel,
    panel: {
      open: true,
      sliderDraggingRef: { current: false },
      columnName: filterPanelColumn ?? "",
      columnDisplayName: filterPanelDisplayName,
      filterType: filterPanelFilterType,
      dataType: filterPanelColDef?.dataType ?? null,
      valueMultiplier: filterValueMultiplier(filterPanelSubType),
      valueSubType: filterPanelSubType,
      onBackToMenu: closeFilterPanel,
      searchQuery: panelValueSearch,
      onSearchQueryChange: setPanelValueSearch,
      uniqueValues: filterPanelUniqueValues,
      valueCounts: filterPanelValueCounts,
      selectedValues: panelSelectedValues,
      onToggleValue: (valueOrIds) =>
        setPanelSelectedValues((prev) =>
          toggleSelectedValues(
            prev,
            valueOrIds,
            (filterPanelColDef?.type ??
              summaryColumnTypes[filterPanelColumn]?.type) === BOOL_COLUMN_TYPE
          )
        ),
      onClearSelectedValues: () => setPanelSelectedValues(new Set()),
      valuesEmptyMessage: summaryFailed
        ? translateLabels(labels.OPTION_FILTER_VALUES_UNAVAILABLE, {
            default: "Values could not be loaded",
          })
        : undefined,
      getValueDisplayLabel: getFilterPanelEnumDisplayValue,
      getValueStartIcon: null,
      minMaxValues: filterPanelNumberBounds,
      numberRange: panelNumberRange,
      onNumberRangeChange: setPanelNumberRange,
      onClearNumberMin: () =>
        setPanelNumberRange((prev) => ({ ...prev, min: null })),
      onClearNumberMax: () =>
        setPanelNumberRange((prev) => ({ ...prev, max: null })),
      numberBlankCount: filterPanelColumnSummary?.blank ?? 0,
      onToggleNumberBlank: () => {},
      onClearNumberBlank: () => {},
      histogramBins: filterPanelHistogramBins,
      dateRange: panelDateRange,
      onDateRangeChange: setPanelDateRange,
      dateFormat,
      formatDateInputValue: formatDateInput,
      parseDateInputValue: parseDateInput,
      onClearDateFrom: () =>
        setPanelDateRange((prev) => ({ ...prev, from: null })),
      onClearDateTo: () => setPanelDateRange((prev) => ({ ...prev, to: null })),
      dateBlankCount: filterPanelDateBlankCount,
      onToggleDateBlank: () => {},
      onClearDateBlank: () => {},
      numberActions: panelActions,
      valueActions: panelActions,
      dateActions: panelActions,
    },
  };

  return {
    preAggFilterCols,
    openFilterPanel,
    buildPreAggFilters,
    filterMaps,
    setFilterMaps,
    panelProps,
  };
};
